import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../services/api';

interface ScreenDef {
  route: string;
  name: string;
}

interface VisibilityRule {
  id: number;
  email_pattern: string;
  pattern_type: 'domain' | 'email';
  screen_route: string;
  screen_name: string;
  access: 'hidden' | 'visible';
  created_by: string;
  created_at: string;
}

const ScreenVisibilityControl: React.FC = () => {
  const { currentUser } = useAuth();
  const [screens, setScreens] = useState<ScreenDef[]>([]);
  const [rules, setRules] = useState<VisibilityRule[]>([]);
  const [patterns, setPatterns] = useState<string[]>([]);
  const [selectedPattern, setSelectedPattern] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const isSoftura = currentUser?.email?.split('@')[1]?.toLowerCase() === 'softura.com';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [screensRes, rulesRes] = await Promise.all([
        authFetch('/api/screen-visibility/screens'),
        authFetch('/api/screen-visibility/rules')
      ]);

      if (screensRes.ok) {
        const screensData = await screensRes.json();
        setScreens(screensData);
      }

      if (rulesRes.ok) {
        const rulesData = await rulesRes.json();
        setRules(rulesData);

        // Extract unique patterns from DB rules + any manually added ones
        const dbPatterns = rulesData.map((r: VisibilityRule) => r.email_pattern);
        const allPatterns = new Set([...dbPatterns]);
        const uniquePatterns = Array.from(allPatterns) as string[];
        // Sort: * first, then domains, then emails
        uniquePatterns.sort((a: string, b: string) => {
          if (a === '*') return -1;
          if (b === '*') return 1;
          const aIsEmail = a.includes('@');
          const bIsEmail = b.includes('@');
          if (aIsEmail && !bIsEmail) return 1;
          if (!aIsEmail && bIsEmail) return -1;
          return a.localeCompare(b);
        });
        setPatterns(uniquePatterns);

        // Auto-select first pattern if none selected
        if (!selectedPattern && uniquePatterns.length > 0) {
          setSelectedPattern(uniquePatterns[0]);
        }
      }
    } catch (err) {
      console.error('Error loading screen visibility data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPattern]);

  useEffect(() => {
    if (isSoftura) {
      loadData();
    }
  }, [isSoftura, loadData]);

  // Check if a screen is hidden for a specific pattern
  const isHidden = (pattern: string, route: string): boolean => {
    const rule = rules.find(r => r.email_pattern === pattern && r.screen_route === route);
    return rule?.access === 'hidden';
  };

  // Check if a screen has an explicit "visible" override (for email overrides)
  const isExplicitlyVisible = (pattern: string, route: string): boolean => {
    const rule = rules.find(r => r.email_pattern === pattern && r.screen_route === route);
    return rule?.access === 'visible';
  };

  // Get email overrides for a specific screen route under the selected domain
  const getEmailOverrides = (route: string): VisibilityRule[] => {
    if (!selectedPattern || selectedPattern.includes('@')) return [];
    // Find email-level rules for this screen where the email belongs to the selected domain pattern
    return rules.filter(r =>
      r.pattern_type === 'email' &&
      r.screen_route === route &&
      (selectedPattern === '*' || r.email_pattern.endsWith('@' + selectedPattern))
    );
  };

  const handleToggleHidden = async (pattern: string, screen: ScreenDef) => {
    try {
      setSaving(true);
      const currentlyHidden = isHidden(pattern, screen.route);

      if (currentlyHidden) {
        // Remove the rule → screen becomes visible (default)
        const rule = rules.find(r => r.email_pattern === pattern && r.screen_route === screen.route);
        if (rule) {
          await authFetch(`/api/screen-visibility/rules/${rule.id}`, { method: 'DELETE' });
        }
      } else {
        // Create a "hidden" rule
        const patternType = pattern.includes('@') ? 'email' : 'domain';
        await authFetch('/api/screen-visibility/rules', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email_pattern: pattern,
            pattern_type: patternType,
            screen_route: screen.route,
            screen_name: screen.name,
            access: 'hidden'
          })
        });
      }

      await loadData();
    } catch (err) {
      console.error('Error updating rule:', err);
      alert('Error updating rule');
    } finally {
      setSaving(false);
    }
  };

  // For email overrides: toggle between "visible" override and removing the override
  const handleToggleEmailOverride = async (pattern: string, screen: ScreenDef) => {
    try {
      setSaving(true);
      const explicitlyVisible = isExplicitlyVisible(pattern, screen.route);

      if (explicitlyVisible) {
        // Remove the override → inherits from domain rule
        const rule = rules.find(r => r.email_pattern === pattern && r.screen_route === screen.route);
        if (rule) {
          await authFetch(`/api/screen-visibility/rules/${rule.id}`, { method: 'DELETE' });
        }
      } else {
        // Create a "visible" override
        await authFetch('/api/screen-visibility/rules', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email_pattern: pattern,
            pattern_type: 'email',
            screen_route: screen.route,
            screen_name: screen.name,
            access: 'visible'
          })
        });
      }

      await loadData();
    } catch (err) {
      console.error('Error updating rule:', err);
      alert('Error updating rule');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDomain = async () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    if (domain === 'softura.com') {
      alert('Cannot add restrictions for softura.com — this is the controlling domain.');
      return;
    }
    if (patterns.includes(domain)) {
      setSelectedPattern(domain);
      setShowAddDomain(false);
      setNewDomain('');
      return;
    }
    // Save a placeholder rule to DB so the pattern persists across reloads
    try {
      await authFetch('/api/screen-visibility/rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_pattern: domain,
          pattern_type: 'domain',
          screen_route: '__placeholder__',
          screen_name: '__placeholder__',
          access: 'visible'
        })
      });
      await loadData();
      setSelectedPattern(domain);
    } catch (err) {
      console.error('Error adding domain:', err);
    }
    setShowAddDomain(false);
    setNewDomain('');
  };

  const handleAddEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }
    if (email.endsWith('@softura.com')) {
      alert('Cannot add restrictions for @softura.com emails — this is the controlling domain.');
      return;
    }
    if (patterns.includes(email)) {
      setSelectedPattern(email);
      setShowAddEmail(false);
      setNewEmail('');
      return;
    }
    // Save a placeholder rule to DB so the pattern persists across reloads
    try {
      await authFetch('/api/screen-visibility/rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_pattern: email,
          pattern_type: 'email',
          screen_route: '__placeholder__',
          screen_name: '__placeholder__',
          access: 'visible'
        })
      });
      await loadData();
      setSelectedPattern(email);
    } catch (err) {
      console.error('Error adding email:', err);
    }
    setShowAddEmail(false);
    setNewEmail('');
  };

  const handleDeletePattern = async (pattern: string) => {
    try {
      setSaving(true);
      await authFetch(`/api/screen-visibility/rules/pattern/${encodeURIComponent(pattern)}`, {
        method: 'DELETE'
      });
      setPatterns(prev => prev.filter(p => p !== pattern));
      if (selectedPattern === pattern) {
        setSelectedPattern(patterns.find(p => p !== pattern) || '');
      }
      setShowDeleteConfirm(null);
      await loadData();
    } catch (err) {
      console.error('Error deleting pattern:', err);
      alert('Error deleting rules');
    } finally {
      setSaving(false);
    }
  };

  if (!isSoftura) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <h1 className="page-header__title">Access Denied</h1>
          </div>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          <p>Only @softura.com administrators can access Screen Visibility Control.</p>
        </div>
      </div>
    );
  }

  const selectedIsEmail = selectedPattern.includes('@');
  const selectedIsDomain = !selectedIsEmail && selectedPattern !== '';

  // Count hidden screens for each pattern
  const getHiddenCount = (pattern: string): number => {
    return rules.filter(r => r.email_pattern === pattern && r.access === 'hidden' && r.screen_route !== '__placeholder__').length;
  };

  return (
    <div className="page page-fixed-header">
      <div className="fixed-header-section">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Screen Visibility Control</h1>
            </div>
            <div className="page-header__actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowAddDomain(true)}
              >
                + Add Domain
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => setShowAddEmail(true)}
              >
                + Add Email Override
              </button>
            </div>
          </div>
        </div>

        {/* Pattern Selector */}
        <div style={{ padding: '12px 0', borderBottom: '1px solid #dee2e6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Viewing rules for:
            </label>
            <select
              value={selectedPattern}
              onChange={(e) => setSelectedPattern(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                minWidth: '300px',
                backgroundColor: 'white'
              }}
            >
              <option value="">-- Select Domain or Email --</option>
              {patterns.map(p => (
                <option key={p} value={p}>
                  {p === '*' ? '* (All non-softura.com users)' : p}
                  {` — ${getHiddenCount(p)} screen(s) hidden`}
                </option>
              ))}
            </select>
            {selectedPattern && selectedPattern !== '*' && (
              <button
                onClick={() => setShowDeleteConfirm(selectedPattern)}
                className="btn btn-outline-danger"
                style={{ fontSize: '13px', padding: '6px 12px' }}
                title={`Remove all rules for ${selectedPattern}`}
              >
                Remove {selectedPattern}
              </button>
            )}
            <span style={{ fontSize: '13px', color: '#6b7280', marginLeft: 'auto' }}>
              Priority: Exact email &gt; Specific domain &gt; Wildcard (*) &gt; Visible
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="scrollable-data-section" style={{ overflowX: 'auto' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading...</div>
        ) : !selectedPattern ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            <p style={{ fontSize: '15px' }}>Select a domain or email pattern above to manage screen visibility rules.</p>
            <p style={{ fontSize: '13px', color: '#999' }}>
              The wildcard <strong>*</strong> applies to all users outside @softura.com.
              Specific domain rules (e.g. gmail.com) override the wildcard.
              Specific email rules (e.g. user@gmail.com) override domain rules.
            </p>
          </div>
        ) : (
          <div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', minWidth: '300px' }}>Screen</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '120px' }}>Hidden</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '140px' }}>Status</th>
                  {selectedIsDomain && (
                    <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '150px' }}>Email Overrides</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {screens.map((screen) => {
                  const hidden = isHidden(selectedPattern, screen.route);
                  const overrides = getEmailOverrides(screen.route);

                  return (
                    <React.Fragment key={screen.route}>
                      <tr style={{
                        borderBottom: '1px solid #eee',
                        backgroundColor: hidden ? '#fef2f2' : 'transparent'
                      }}>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ fontWeight: '500', color: hidden ? '#991b1b' : '#333' }}>{screen.name}</div>
                          <div style={{ fontSize: '11px', color: '#999' }}>{screen.route}</div>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={hidden}
                            onChange={() => handleToggleHidden(selectedPattern, screen)}
                            disabled={saving}
                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                          />
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          {hidden ? (
                            <span style={{
                              padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                              backgroundColor: '#fee2e2', color: '#dc2626'
                            }}>
                              HIDDEN
                            </span>
                          ) : (
                            <span style={{
                              padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                              backgroundColor: '#dcfce7', color: '#16a34a'
                            }}>
                              VISIBLE
                            </span>
                          )}
                        </td>
                        {selectedIsDomain && (
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            {overrides.length > 0 && (
                              <span style={{
                                padding: '2px 8px',
                                backgroundColor: '#e0f2fe',
                                color: '#0369a1',
                                borderRadius: '10px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                {overrides.length} override{overrides.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                      {/* Show email overrides inline */}
                      {selectedIsDomain && overrides.length > 0 && overrides.map(override => (
                        <tr key={override.id} style={{ backgroundColor: '#f0f9ff', borderBottom: '1px solid #e0f2fe' }}>
                          <td style={{ padding: '6px 12px 6px 36px', fontSize: '13px' }}>
                            <span style={{ color: '#0369a1' }}>{override.email_pattern}</span>
                            <span style={{ color: '#999', marginLeft: '8px' }}>(email override)</span>
                          </td>
                          <td style={{ padding: '6px 12px', textAlign: 'center' }}></td>
                          <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                            <span style={{
                              padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                              backgroundColor: override.access === 'visible' ? '#dcfce7' : '#fee2e2',
                              color: override.access === 'visible' ? '#16a34a' : '#dc2626'
                            }}>
                              {override.access === 'visible' ? 'VISIBLE' : 'HIDDEN'}
                            </span>
                          </td>
                          <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                            <button
                              onClick={async () => {
                                await authFetch(`/api/screen-visibility/rules/${override.id}`, { method: 'DELETE' });
                                await loadData();
                              }}
                              style={{
                                padding: '2px 8px',
                                fontSize: '11px',
                                border: '1px solid #dc2626',
                                borderRadius: '4px',
                                backgroundColor: 'transparent',
                                color: '#dc2626',
                                cursor: 'pointer'
                              }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Legend */}
            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', color: '#6b7280' }}>
              <strong>How it works:</strong>
              <span style={{ marginLeft: '12px' }}>Check the <strong>Hidden</strong> checkbox to block a screen.</span>
              <span style={{ marginLeft: '12px' }}>Uncheck to make it visible again.</span>
              <span style={{ marginLeft: '12px' }}>Email overrides let specific users see screens that are blocked for their domain.</span>
            </div>
          </div>
        )}
      </div>

      {/* Add Domain Modal */}
      {showAddDomain && (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setShowAddDomain(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{
            background: 'white', borderRadius: '12px', padding: '24px',
            minWidth: '400px', maxWidth: '500px', boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)'
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: '500' }}>Add Domain Rule</h2>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
              Add an email domain to manage screen visibility for all users with that domain.
              Use <strong>*</strong> for all non-softura.com users.
            </p>
            <input
              type="text"
              value={newDomain}
              onChange={e => setNewDomain(e.target.value)}
              placeholder="e.g. gmail.com or clientcorp.com or *"
              style={{
                width: '100%', padding: '10px 12px', fontSize: '14px',
                border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box', marginBottom: '16px'
              }}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleAddDomain()}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="btn btn-secondary" onClick={() => setShowAddDomain(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddDomain}>Add Domain</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Email Override Modal */}
      {showAddEmail && (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setShowAddEmail(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{
            background: 'white', borderRadius: '12px', padding: '24px',
            minWidth: '400px', maxWidth: '500px', boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)'
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: '500' }}>Add Email Override</h2>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
              Add a specific email address to override domain-level rules.
              For example, if gmail.com is blocked from a screen, you can make a specific @gmail.com user visible.
            </p>
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              placeholder="e.g. gorluc@gmail.com"
              style={{
                width: '100%', padding: '10px 12px', fontSize: '14px',
                border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box', marginBottom: '16px'
              }}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleAddEmail()}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="btn btn-secondary" onClick={() => setShowAddEmail(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddEmail}>Add Email</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{
            background: 'white', borderRadius: '12px', padding: '24px',
            minWidth: '400px', maxWidth: '500px', boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)'
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: '500', color: '#dc2626' }}>Remove All Rules</h2>
            <p style={{ fontSize: '14px', color: '#333', marginBottom: '16px' }}>
              Are you sure you want to remove all visibility rules for <strong>{showDeleteConfirm}</strong>?
              This will reset all screens to their default visibility for this {showDeleteConfirm.includes('@') ? 'email' : 'domain'}.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDeletePattern(showDeleteConfirm)}>
                Remove All Rules
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenVisibilityControl;
